import React, { useState, useEffect } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, collection, getDocs, addDoc } from "firebase/firestore";
import { storage, db, auth } from "../firebase";
import Select from "react-select/creatable";
import CropImageModal from "./CropImageModal";

function AddPersonForm({ updateRosterPeople, people, customFields, closeModal, rosterId }) {
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [details, setDetails] = useState("");
  const [socialMedia, setSocialMedia] = useState("");
  const [metOn, setMetOn] = useState("Hinge");
  const [pictureFile, setPictureFile] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [customFieldValues, setCustomFieldValues] = useState(
    customFields.reduce((acc, field) => {
      acc[field.name] = field.type === "scale" ? 0 : "";
      return acc;
    }, {})
  );
  const [tags, setTags] = useState([]); // Global tags fetched from Firestore
  const [selectedTags, setSelectedTags] = useState([]); // Tags selected for this person
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Fetch global tags from Firestore
  useEffect(() => {
    const fetchGlobalTags = async () => {
      try {
        const tagsCollection = collection(db, "globalTags");
        const tagSnapshot = await getDocs(tagsCollection);
        const fetchedTags = tagSnapshot.docs.map((doc) => ({
          value: doc.data().name,
          label: doc.data().name,
        }));
        setTags(fetchedTags);
      } catch (error) {
        console.error("Error fetching global tags:", error);
      }
    };

    fetchGlobalTags();
  }, []);

  const handleCustomFieldChange = (fieldName, value) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleTagChange = async (selectedOptions) => {
    const newTags = selectedOptions.map((option) => option.value);
    setSelectedTags(newTags);

    // Dynamically create new tags in Firestore if not already global
    const existingTagNames = tags.map((tag) => tag.value);
    const newGlobalTags = newTags.filter((tag) => !existingTagNames.includes(tag));

    for (const tag of newGlobalTags) {
      try {
        const tagRef = collection(db, "globalTags");
        await addDoc(tagRef, { name: tag });
        setTags((prevTags) => [...prevTags, { value: tag, label: tag }]);
      } catch (error) {
        console.error(`Error adding global tag: ${tag}`, error);
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setOriginalFile(file); // Save the original file for later use
      const reader = new FileReader();
      reader.onload = () => {
        setTempImageFile(reader.result);
        setIsCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please upload a valid image file.");
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      if (!croppedBlob) {
        console.error("No cropped image provided.");
        return;
      }
      setPictureFile(croppedBlob);
      setIsCropModalOpen(false);
    } catch (error) {
      console.error("Error handling cropped image:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !age) {
      alert("Name and age are required.");
      return;
    }

    try {
      setUploading(true);

      let pictureURL = null;
      let originalPictureURL = null;

      if (pictureFile) {
        const storagePath = `users/${auth.currentUser?.uid}/rosters/${rosterId}/people/${name}-${Date.now()}/cropped-picture`;
        const croppedStorageRef = ref(storage, storagePath);
        await uploadBytes(croppedStorageRef, pictureFile);
        pictureURL = await getDownloadURL(croppedStorageRef);

        if (originalFile) {
          const originalStoragePath = `users/${auth.currentUser?.uid}/rosters/${rosterId}/people/${name}-${Date.now()}/original-picture`;
          const originalStorageRef = ref(storage, originalStoragePath);
          await uploadBytes(originalStorageRef, originalFile);
          originalPictureURL = await getDownloadURL(originalStorageRef);
        }
      }

      const newPerson = {
        id: `${name}-${Date.now()}`,
        name,
        age,
        details,
        socialMedia,
        metOn,
        picture: pictureURL,
        originalPicture: originalPictureURL,
        customFields: customFieldValues,
        tags: selectedTags, // Save selected tags
      };

      const personRef = doc(db, "users", auth.currentUser?.uid, "rosters", rosterId, "people", newPerson.id);
      await setDoc(personRef, newPerson);

      const updatedPeople = [...people, newPerson];
      updateRosterPeople(updatedPeople);
      closeModal();
    } catch (error) {
      console.error("Error adding person:", error);
      alert("Failed to add person. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        backgroundColor: "#ffffff",
        padding: "20px",
        borderRadius: "12px",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        gap: "15px",
        maxWidth: "400px",
        maxHeight: "80vh",
        overflowY: "auto",
        margin: "0 auto",
      }}
    >
      <h2 style={{ fontSize: "24px", textAlign: "center" }}>Add Person</h2>
      <input
        type="text"
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        style={inputStyles}
      />
      <input
        type="number"
        placeholder="Age"
        value={age}
        onChange={(e) => setAge(e.target.value)}
        required
        style={inputStyles}
      />
      <textarea
        placeholder="Details"
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        style={{ ...inputStyles, minHeight: "60px" }}
      />
      <input
        type="url"
        placeholder="Social Media Link"
        value={socialMedia}
        onChange={(e) => setSocialMedia(e.target.value)}
        style={inputStyles}
      />
      <div>
        <label>Met On:</label>
        <select
          value={metOn}
          onChange={(e) => setMetOn(e.target.value)}
          style={{ ...inputStyles, padding: "8px" }}
        >
          <option value="Hinge">Hinge</option>
          <option value="Raya">Raya</option>
          <option value="Tinder">Tinder</option>
          <option value="Feeld">Feeld</option>
          <option value="Bumble">Bumble</option>
          <option value="Real Life">Real Life</option>
        </select>
      </div>
      <input
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        style={{
          padding: "10px",
          border: "1px solid #ddd",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      />
      <h3>Custom Fields</h3>
      {customFields.map((field, index) => (
        <div key={index}>
          <label>{field.name}:</label>
          {field.type === "text" && (
            <input
              type="text"
              value={customFieldValues[field.name] || ""}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
              style={inputStyles}
            />
          )}
          {field.type === "scale" && (
            <input
              type="range"
              min="0"
              max="5"
              value={customFieldValues[field.name] || "0"}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
              style={{ width: "100%" }}
            />
          )}
        </div>
      ))}
      <h3>Tags</h3>
      <Select
        isMulti
        options={tags}
        onChange={handleTagChange}
        placeholder="Select or create tags"
        styles={{
          container: (provided) => ({ ...provided, marginTop: "10px" }),
        }}
      />
      <button
        type="submit"
        style={{
          backgroundColor: "#5a31f4",
          color: "white",
          border: "none",
          borderRadius: "8px",
          padding: "12px",
        }}
      >
        {uploading ? "Uploading..." : "Add Person"}
      </button>
      {isCropModalOpen && (
        <CropImageModal
          imageFile={tempImageFile}
          onCropComplete={handleCropComplete}
          onClose={() => setIsCropModalOpen(false)}
        />
      )}
    </form>
  );
}

const inputStyles = {
  padding: "10px",
  fontSize: "14px",
  border: "1px solid #e1e1e1",
  borderRadius: "8px",
  width: "100%",
  outline: "none",
};

export default AddPersonForm;